import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "src/components/ui/dialog";
import CursorIcon from "src/components/ui/icons/cursor";
import { getProductName } from "src/utils/brandTheme";

import CursorIntegrationForm from "./CursorIntegrationForm";

interface CursorIntegrationProps {
  isOpen: boolean;
  onClose: () => void;
  onRefetch?: () => void;
  brandTheme?: string | null;
}

const CursorInstructions: React.FC<{ productName: string }> = ({ productName }) => (
  <div className="text-muted-foreground space-y-3">
    <div className="mb-4">
      <p className="mb-2 font-bold">Generate a Cursor API key:</p>
      <ol className="text-sm space-y-2 list-decimal list-inside">
        <li>
          Go to{" "}
          <a
            href="https://cursor.com/dashboard/integrations"
            target="_blank"
            rel="noopener noreferrer"
            className="text-amber-600 underline"
          >
            cursor.com/dashboard/integrations
          </a>
          .
        </li>
        <li>
          Under <span className="text-amber-600">API Keys</span>, click{" "}
          <span className="text-amber-600">Create new key</span>.
        </li>
        <li>Name the key (e.g. {`"${productName}"`}) and copy the generated value.</li>
        <li>Paste the API key into the field below and click Test Connection.</li>
      </ol>
    </div>
    <div className="mb-4">
      <p className="mb-2 font-bold">Required Cursor account setup:</p>
      <ul className="text-sm space-y-1 list-disc list-inside">
        <li>Cloud Agents enabled with usage-based pricing.</li>
        <li>GitHub connected with access to the repos you want {productName} to delegate work to.</li>
        <li>A default repository configured in the Cursor dashboard (used as fallback).</li>
      </ul>
    </div>
  </div>
);

export default function CursorIntegration({ isOpen, onClose, onRefetch, brandTheme }: CursorIntegrationProps) {
  const [showInstructions, setShowInstructions] = useState(false);
  const productName = getProductName(brandTheme);

  const handleFormComplete = (success: boolean) => {
    if (success) {
      onClose();
      onRefetch?.();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl bg-popover text-foreground border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <CursorIcon className="h-6 w-6" />
            Connect Cursor
          </DialogTitle>
          <DialogDescription>
            Let {productName} delegate proposal implementations to Cursor's Cloud Agents.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 overflow-y-auto max-h-[80vh] p-1">
          <div className="space-y-3">
            <button
              onClick={() => setShowInstructions(!showInstructions)}
              className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
            >
              {showInstructions ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              {showInstructions ? "Hide instructions" : "Show instructions"}
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                showInstructions ? "max-h-[2000px] opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <CursorInstructions productName={productName} />
            </div>
          </div>

          <CursorIntegrationForm onComplete={handleFormComplete} onCancel={onClose} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
